import type React from "react";

import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";

import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Typography,
  IconButton,
  Divider,
  TextField,
  Fab,
} from "@mui/material";
import {
  ArrowLeft,
  Mail,
  Phone,
  MessageCircleMore,
  CirclePlus,
  Trash,
  CircleCheck,
  CircleX,
  Edit,
  Trash2,
} from "lucide-react";
import { useLead, useNegotiation } from "../services/useLeads";
import { openWhatsApp, formatPhone, formatDate } from "../utils/contact";
// import { isMobile } from "react-device-detect";
import { ACTIVE_COLOR, DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";
import createNote from "@/services/notes/create";
import deleteNote from "@/services/notes/delete";
import updateNote from "@/services/notes/update";
import updateNegotiation from "@/services/leads/negotiation";
import registerAction from "@/services/leads/action";
import deleteOne from "@/services/leads/delete";
import { LeadDrawer } from "@/components/forms/Lead";

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Configurar locale y timezone a CDMX
dayjs.locale("es");
dayjs.tz.setDefault("America/Mexico_City");

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: lead, isLoading, error, refetch } = useLead(id!);
  const [noteText, setNoteText] = useState("");
  const [selectedNote, setSelectedNote] = useState("");
  const [leadOpen, setLeadOpen] = useState(false);

  const handleBack = () => {
    navigate("/leads");
  };

  const handleAddNote = async () => {
    if (noteText.trim()) {
      await createNote(id || "", noteText, "lead");
      setNoteText("");
      refetch();
    }
  };

  const handleWhatsApp = async () => {
    if (lead?.phone) {
      await registerAction({
        leadId: lead._id!,
        action: "Seguimiento por mensaje de Whatsapp",
      });
      openWhatsApp(lead.phone);
    }
  };

  const handleConvertLead = async () => {
    await updateNegotiation({ leadId: id!, negotiation: true });
    navigate("/deals");
  };

  const handleCancelLead = async () => {
    await updateNegotiation({ leadId: id!, negotiation: false });
    navigate("/leads");
  };

  const handleDeleteNote = async () => {
    await deleteNote(selectedNote);
    setSelectedNote("");
  };

  const handleUpdateNote = async () => {
    await updateNote(selectedNote, noteText);
  };

  useEffect(() => {
    if (selectedNote && noteText) {
      handleUpdateNote();
    }
  }, [selectedNote, noteText]);

  const handleDeleteLead = async () => {
    await deleteOne(lead?._id!);
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%" }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al cargar el lead. Por favor, inténtalo de nuevo.
        </Alert>
        <Button startIcon={<ArrowLeft />} onClick={handleBack}>
          Volver a Leads
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} size={{ xs: 12 }} sx={{ p: 2 }}>
      <Paper
        elevation={3}
        sx={{ width: "100%", padding: 2, minHeight: "100vh" }}
      >
        <Grid container spacing={2}>
          <Grid container size={{ xs: 12 }} sx={{ minHeight: 100 }}>
            <Box
              sx={{
                p: 2,
                width: "100%",
              }}
            >
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid size={{ xs: 9 }}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid>
                      <Typography
                        fontWeight="bold"
                        variant="h4"
                        color={DEFAULT_COLOR}
                      >
                        {lead?.fullName}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider />
                <Grid size={{ xs: 3 }}>
                  <Grid container justifyContent="flex-end" spacing={1}>
                    <Grid>
                      <IconButton
                        color="primary"
                        style={{
                          border: `1px solid ${YELLOW_COLOR}`,
                        }}
                        disabled={!lead?.email}
                        component="a"
                        onClick={async () => {
                          await registerAction({
                            leadId: lead._id!,
                            action: "Seguimiento por Correo Electrónico",
                          });
                        }}
                        href={`mailto:${lead.email}?subject=Seguimiento&body=Seguimiento`}
                      >
                        <Mail style={{ color: YELLOW_COLOR }} />
                      </IconButton>
                    </Grid>
                    <Grid>
                      <IconButton
                        color="primary"
                        style={{
                          border: `1px solid ${YELLOW_COLOR}`,
                        }}
                        component="a"
                        disabled={!lead?.phone}
                        onClick={async () => {
                          await registerAction({
                            leadId: lead._id!,
                            action: "Seguimiento por Llamada",
                          });
                        }}
                        href={`tel:${formatPhone(lead.phone || "")}`}
                      >
                        <Phone
                          style={{
                            color: YELLOW_COLOR,
                          }}
                        />
                      </IconButton>
                    </Grid>
                    <Grid>
                      <IconButton
                        color="primary"
                        style={{
                          border: `1px solid ${YELLOW_COLOR}`,
                        }}
                        onClick={handleWhatsApp}
                      >
                        <MessageCircleMore style={{ color: YELLOW_COLOR }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid>
                <Button
                  startIcon={<CircleCheck />}
                  sx={{
                    backgroundColor: YELLOW_COLOR,
                    color: "#fff",
                    minWidth: 200,
                  }}
                  fullWidth
                  onClick={() => handleConvertLead()}
                >
                  Ganado
                </Button>
              </Grid>
              <Grid>
                <Button
                  startIcon={<CircleX />}
                  sx={{
                    backgroundColor: "rgb(242,6,23)",
                    color: "#fff",
                    minWidth: 200,
                  }}
                  onClick={() => handleCancelLead()}
                >
                  Perdido
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Divider sx={{ width: "100%" }} />
          <Grid container spacing={2} size={{ xs: 12 }} sx={{ minHeight: 100 }}>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  overflowY: "auto",
                  backgroundColor: YELLOW_COLOR,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.user ? `${lead.user.name} ${lead.user.lastName}` : "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Asesor a Cargo
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  overflowY: "auto",
                  backgroundColor: ACTIVE_COLOR,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.type ?? "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Origen de Contacto
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.email || "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Correo Electrónico
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.phone ? formatPhone(lead?.phone) : "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Número Telefónico
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.campaign || "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Desarrollo
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.landMeasurement || "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Medidas
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.hitch || "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Enganche
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.createdAt ? dayjs(lead.createdAt).format("lll") : "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Fecha de Creación
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }} sx={{ alignContent: "center" }}>
              <Paper
                elevation={3}
                sx={{
                  minHeight: 70,
                  maxHeight: 70,
                  alignContent: "center",
                  textAlign: "center",
                  p: 2,
                  backgroundColor: ACTIVE_COLOR,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#fff", fontSize: 18 }}
                >
                  {lead?.appointmentDate
                    ? dayjs(lead.appointmentDate).add(1, "hour").format("LLLL")
                    : "-"}
                </Typography>
                <Typography
                  sx={{ color: "#fff", fontWeight: "bold", fontSize: 16 }}
                >
                  Fecha de Cita
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <Divider sx={{ width: "100%" }} />
          <Grid
            container
            spacing={2}
            size={{ xs: 12 }}
            sx={{ minHeight: "80vh", maxHeight: "80vh", overflowY: "auto" }}
          >
            <Grid size={{ xs: 12, lg: 8 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: ACTIVE_COLOR }}
              >
                Notas
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  container
                  spacing={2}
                  size={{ xs: 12 }}
                  justifyContent="flex-end"
                  textAlign="end"
                >
                  <Grid
                    container
                    size={{ xs: 12, md: 4 }}
                    direction="row"
                    sx={{ mb: 2 }}
                  >
                    <Grid
                      size={{ xs: 12, md: 6 }}
                      sx={{ cursor: "pointer" }}
                      key="add"
                    >
                      <Button
                        startIcon={<CirclePlus />}
                        onClick={handleAddNote}
                        fullWidth
                        variant="contained"
                        sx={{
                          borderRadius: 3,
                          px: 4,
                          backgroundColor: YELLOW_COLOR,
                        }}
                      >
                        Agregar
                      </Button>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 6 }}
                      sx={{ cursor: "pointer" }}
                      key="delete"
                    >
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteNote}
                        sx={{ borderRadius: 3, px: 4 }}
                        fullWidth
                        disabled={!selectedNote}
                        startIcon={<Trash />}
                      >
                        Eliminar
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label=""
                    multiline
                    rows={10}
                    variant="outlined"
                    fullWidth
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    maxHeight: "40vh",
                    overflowY: "auto",
                    mt: 2,
                    minWidth: "100%",
                  }}
                >
                  <Grid
                    container
                    size={{ xs: 12 }}
                    spacing={2}
                    sx={{ mt: 2, p: 2 }}
                  >
                    {(lead?.notes || [])
                      .sort(
                        (a, b) =>
                          dayjs(b.createdAt).valueOf() -
                          dayjs(a.createdAt).valueOf()
                      )
                      .map((note) => {
                        const isActive = selectedNote === note._id;
                        return (
                          <Grid size={{ xs: 12 }} sx={{ cursor: "pointer" }}>
                            <Paper
                              elevation={3}
                              sx={{
                                p: 2,
                                minHeight: 70,
                                maxHeight: 70,
                                backgroundColor: isActive
                                  ? ACTIVE_COLOR
                                  : "#fff",
                                // minWidth: "100%",
                              }}
                              onClick={() => {
                                setNoteText(note.note);
                                setSelectedNote(note._id);
                              }}
                            >
                              <Typography
                                fontWeight="bold"
                                sx={{ color: isActive ? "#fff" : ACTIVE_COLOR }}
                              >{`${note?.user?.name} ${note?.user?.lastName}`}</Typography>
                              <Typography
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: 12,
                                  color: isActive ? "#fff" : "text.secondary",
                                }}
                              >
                                {note.note}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  textAlign: "end",
                                  mt: 2,
                                  color: isActive ? "#fff" : "#e3e3e3",
                                }}
                              >
                                {dayjs(note.createdAt).format("lll")}
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: ACTIVE_COLOR }}
              >
                Historial
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  p: 2,
                  maxHeight: "50vh",
                  overflowY: "auto",
                }}
              >
                {(lead?.history || [])
                  .sort(
                    (a, b) =>
                      dayjs(b.createdAt).valueOf() -
                      dayjs(a.createdAt).valueOf()
                  )
                  .map((history) => (
                    <Grid size={{ xs: 12 }} key={history._id}>
                      <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography
                          sx={{ color: ACTIVE_COLOR }}
                          fontWeight="bold"
                        >
                          {history.type}
                        </Typography>
                        <Typography variant="body2">
                          {history.comment}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#e3e3e3",
                            fontSize: 10,
                            textAlign: "end",
                            mt: 2,
                          }}
                        >
                          {dayjs(history.createdAt).format("lll")}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Fab
        aria-label="edit-record"
        sx={{
          backgroundColor: YELLOW_COLOR,
          color: "white",
          position: "fixed",
          bottom: { xs: 150, sm: 150 },
          right: 16,
          zIndex: 3000,
          "&:hover": {
            backgroundColor: YELLOW_COLOR,
          },
          display: leadOpen ? "none" : "",
        }}
        onClick={() => setLeadOpen(true)}
      >
        <Edit />
      </Fab>
      <Fab
        aria-label="delete-record"
        sx={{
          backgroundColor: "#CC0000",
          color: "white",
          position: "fixed",
          bottom: { xs: 80, sm: 80 },
          right: 16,
          zIndex: 3000,
          "&:hover": {
            backgroundColor: "#CC0000",
          },
          display: leadOpen ? "none" : "",
        }}
        onClick={handleDeleteLead}
      >
        <Trash2 />
      </Fab>
      <LeadDrawer
        open={leadOpen}
        onClose={() => {
          setLeadOpen(false);
        }}
        lead={lead}
      />
    </Grid>
  );
}
